/**
 * WorkCard — carte œuvre dans la grille bibliothèque (`WorksPage`).
 *
 * Affiche un badge de cycle de vie (Brouillon "À compléter" / "Prête à
 * publier" → Publiée → Archivée) calculé via `is_publishable` /
 * `missing_for_publish` côté BE (`publication_service`). Le `<KebabMenu>`
 * expose les transitions disponibles selon l'état : Publier sur
 * sgraphic.art / Retirer de sgraphic.art / Archiver / Désarchiver, plus
 * Dupliquer + Supprimer toujours visibles.
 */

import { Link, useNavigate } from 'react-router'
import { Archive, ArchiveRestore, Copy, EyeOff, Pencil, Send, Sparkles, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import Badge, { type BadgeVariant } from '@/components/ui/Badge'
import KebabMenu, { type KebabMenuItem } from '@/components/ui/KebabMenu'
import MediaImage from '@/components/ui/MediaImage'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import { useGenerateDrafts } from '@/hooks/useDrafts'
import {
  useArchiveWork,
  useDeleteWork,
  useDuplicateWork,
  usePublishWorkToPortfolio,
  useUnarchiveWork,
  useUnpublishWorkFromPortfolio,
} from '@/hooks/useWorks'
import type { WorkSummaryResponse } from '@/types/works'

const MISSING_FIELD_FR: Record<string, string> = {
  description_fr: 'description',
  story_fr: 'histoire',
}

function formatMissing(missing: readonly string[]): string {
  const labels = missing.map((field) => MISSING_FIELD_FR[field] ?? field)
  if (labels.length <= 1) return labels.join('')
  if (labels.length === 2) return `${labels[0]} et ${labels[1]}`
  return `${labels.slice(0, -1).join(', ')} et ${labels[labels.length - 1]}`
}

interface ReadinessBadge {
  label: string
  variant: BadgeVariant
  caption?: string
}

function describeReadiness(work: WorkSummaryResponse): ReadinessBadge {
  if (work.status === 'published') return { label: 'Publiée', variant: 'success' }
  if (work.status === 'archived') return { label: 'Archivée', variant: 'warning' }
  if (work.is_publishable) return { label: 'Prête à publier', variant: 'info' }
  const missing = work.missing_for_publish ?? []
  return {
    label: 'À compléter',
    variant: 'default',
    caption: missing.length > 0 ? `Manque ${formatMissing(missing)}` : undefined,
  }
}

interface WorkCardProps {
  work: WorkSummaryResponse
}

export default function WorkCard({ work }: WorkCardProps) {
  const navigate = useNavigate()
  const { confirm } = useConfirm()

  const archiveMutation = useArchiveWork(work.id)
  const unarchiveMutation = useUnarchiveWork(work.id)
  const duplicateMutation = useDuplicateWork()
  const deleteMutation = useDeleteWork()
  const publishMutation = usePublishWorkToPortfolio(work.id)
  const unpublishMutation = useUnpublishWorkFromPortfolio(work.id)
  const generateDraftsMutation = useGenerateDrafts(work.id)

  const handleGenerateDrafts = async () => {
    try {
      const batch = await generateDraftsMutation.mutateAsync(undefined)
      if (batch.scheduled_count === 0) {
        const reason = batch.skipped[0]?.reason
        if (reason === 'no_missing_kinds') {
          toast.info(`« ${work.title_fr} » : tous les drafts éligibles existent déjà.`)
        } else if (reason === 'already_active') {
          toast.info(`« ${work.title_fr} » : une génération est déjà en cours.`)
        } else {
          toast.info(`« ${work.title_fr} » : aucun draft à générer.`)
        }
        return
      }
      // Génération en arrière-plan via la queue (S5.5) — la liste /drafts
      // (cache TanStack) sera invalidée par la page WorkDetailPage si
      // ouverte, sinon au prochain refresh manuel. Ici on se contente
      // d'un toast info (la page Works n'affiche pas les drafts).
      toast.info(
        `Génération lancée en arrière-plan pour « ${work.title_fr} » — ${batch.scheduled_count} job(s) en file.`,
      )
    } catch (err) {
      const detail = (err as { detail?: string })?.detail ?? 'Erreur inconnue'
      toast.error(`Génération impossible : ${detail}`)
    }
  }

  const handlePublish = async () => {
    try {
      await publishMutation.mutateAsync()
      toast.success(`« ${work.title_fr} » publiée sur sgraphic.art.`)
    } catch (err) {
      const detail = (err as { detail?: string })?.detail ?? 'Erreur inconnue'
      toast.error(`Publication impossible : ${detail}`)
    }
  }

  const handleUnpublish = async () => {
    const confirmed = await confirm({
      title: 'Retirer cette œuvre de sgraphic.art ?',
      message: 'Elle repassera en brouillon mais conservera son historique de publication.',
      variant: 'danger',
      confirmText: 'Retirer',
    })
    if (!confirmed) return
    try {
      await unpublishMutation.mutateAsync()
      toast.success(`« ${work.title_fr} » retirée de sgraphic.art.`)
    } catch (err) {
      const detail = (err as { detail?: string })?.detail ?? 'Erreur inconnue'
      toast.error(`Retrait impossible : ${detail}`)
    }
  }

  const handleArchive = async () => {
    const confirmed = await confirm({
      title: 'Archiver cette œuvre ?',
      message:
        'Elle ne sera plus visible dans la bibliothèque par défaut, mais reste consultable via le filtre statut.',
      variant: 'danger',
      confirmText: 'Archiver',
    })
    if (!confirmed) return
    try {
      await archiveMutation.mutateAsync()
      toast.success(`« ${work.title_fr} » archivée.`)
    } catch (err) {
      const detail = (err as { detail?: string })?.detail ?? 'Erreur inconnue'
      toast.error(`Archivage impossible : ${detail}`)
    }
  }

  const handleDuplicate = async () => {
    try {
      const created = await duplicateMutation.mutateAsync(work.id)
      toast.success(`Copie de « ${work.title_fr} » créée.`)
      navigate(`/works/${created.id}/edit`)
    } catch (err) {
      const detail = (err as { detail?: string })?.detail ?? 'Erreur inconnue'
      toast.error(`Duplication impossible : ${detail}`)
    }
  }

  const handleUnarchive = async () => {
    try {
      await unarchiveMutation.mutateAsync()
      toast.success(`« ${work.title_fr} » sortie de l’archive.`)
    } catch (err) {
      const detail = (err as { detail?: string })?.detail ?? 'Erreur inconnue'
      toast.error(`Désarchivage impossible : ${detail}`)
    }
  }

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Supprimer cette œuvre ?',
      message:
        'Action irréversible : l’œuvre, ses images, ses tags et ses drafts seront définitivement supprimés.',
      variant: 'danger',
      confirmText: 'Supprimer définitivement',
    })
    if (!confirmed) return
    try {
      await deleteMutation.mutateAsync(work.id)
      toast.success(`« ${work.title_fr} » supprimée.`)
    } catch (err) {
      const detail = (err as { detail?: string })?.detail ?? 'Erreur inconnue'
      toast.error(`Suppression impossible : ${detail}`)
    }
  }

  const items: KebabMenuItem[] = []
  items.push({
    id: 'edit',
    label: 'Modifier',
    icon: Pencil,
    onSelect: () => navigate(`/works/${work.id}/edit`),
  })
  if (work.status === 'draft' && work.is_publishable) {
    items.push({
      id: 'publish',
      label: 'Publier sur sgraphic.art',
      icon: Send,
      onSelect: handlePublish,
      disabled: publishMutation.isPending,
    })
  }
  if (work.status === 'published') {
    items.push({
      id: 'unpublish',
      label: 'Retirer de sgraphic.art',
      icon: EyeOff,
      onSelect: handleUnpublish,
      disabled: unpublishMutation.isPending,
    })
  }
  items.push({
    id: 'generate-drafts',
    label: 'Générer les drafts',
    icon: Sparkles,
    onSelect: handleGenerateDrafts,
    disabled: generateDraftsMutation.isPending,
  })
  items.push({
    id: 'duplicate',
    label: 'Dupliquer',
    icon: Copy,
    onSelect: handleDuplicate,
    disabled: duplicateMutation.isPending,
  })
  items.push(
    work.status === 'archived'
      ? {
          id: 'unarchive',
          label: 'Désarchiver',
          icon: ArchiveRestore,
          onSelect: handleUnarchive,
          disabled: unarchiveMutation.isPending,
        }
      : {
          id: 'archive',
          label: 'Archiver',
          icon: Archive,
          onSelect: handleArchive,
          disabled: archiveMutation.isPending,
        },
  )
  items.push({
    id: 'delete',
    label: 'Supprimer',
    icon: Trash2,
    variant: 'danger',
    onSelect: handleDelete,
    disabled: deleteMutation.isPending,
  })

  return (
    <li className="works-grid-item">
      <Link to={`/works/${work.id}`} className="works-grid-card">
        <div className="works-grid-thumb">
          <MediaImage minioKey={work.thumb_minio_key ?? null} alt={work.title_fr} />
        </div>
        <div className="works-grid-meta">
          <h3 className="works-grid-title">{work.title_fr}</h3>
          {work.tags && work.tags.length > 0 && (
            <ul className="works-grid-tags" role="list">
              {work.tags.slice(0, 3).map((tag) => (
                <li key={tag.id}>
                  <Badge variant="default" size="sm">
                    {tag.label_fr}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
          <div className="works-grid-footer">
            {(() => {
              const readiness = describeReadiness(work)
              return (
                <>
                  <Badge variant={readiness.variant} size="sm" dot>
                    {readiness.label}
                  </Badge>
                  {readiness.caption && <span className="ui-caption">{readiness.caption}</span>}
                </>
              )
            })()}
          </div>
        </div>
      </Link>
      <div className="works-grid-actions">
        <KebabMenu
          items={items}
          ariaLabel={`Actions sur ${work.title_fr}`}
          buttonVariant="overlay"
        />
      </div>
    </li>
  )
}
